import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an element is near the viewport, and whether it ever has been.
 * Used to defer WebGL context creation until a canvas is approached, and to stop
 * rendering frames the visitor cannot see.
 */
export function useInView<T extends HTMLElement>(rootMargin = '200px') {
  const ref = useRef<T>(null);
  const supportsObserver = typeof IntersectionObserver !== 'undefined';
  const [inView, setInView] = useState(!supportsObserver);
  const [hasBeenInView, setHasBeenInView] = useState(!supportsObserver);

  useEffect(() => {
    const element = ref.current;

    if (!element || !supportsObserver) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);

        if (entry.isIntersecting) {
          setHasBeenInView(true);
        }
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, supportsObserver]);

  return { ref, inView, hasBeenInView };
}
